/**
 * Mappt technische Fehlerklassen auf einen Klartext-Satz für das Statusband (A6).
 * Die technische Meldung bleibt hinter „Details" erreichbar.
 */

export type Fehlerklasse =
  | "timeout"
  | "fehlender_api_schluessel"
  | "not_implemented"
  | "budgetgrenze"
  | "unbekannt";

export type FehlertextErgebnis = {
  klasse: Fehlerklasse;
  /** Satz für das Statusband (ohne Präfix „Fehlgeschlagen:" wo anders laut Tabelle). */
  anzeige: string;
  /** Rohmeldung für Details. */
  technisch: string;
};

function ersteZeile(text: string): string {
  const line = text.split(/\r?\n/).map((l) => l.trim()).find((l) => l.length > 0);
  return line ?? text.trim();
}

/**
 * Erkennt die Fehlerklasse aus Status, Summary oder Freitext.
 */
export function erkenneFehlerklasse(
  roh: string,
  status?: string | null,
): Fehlerklasse {
  const s = (status ?? "").toLowerCase();
  if (s === "not_implemented" || s === "skipped") {
    if (s === "not_implemented" || /not[_\s-]?implemented/i.test(roh)) {
      return "not_implemented";
    }
  }
  if (/not[_\s-]?implemented/i.test(roh)) return "not_implemented";

  if (
    /timeout|zeitüberschreitung|timed?\s*out|zu lange/i.test(roh) ||
    /wartezeit|über 5 minuten/i.test(roh)
  ) {
    return "timeout";
  }

  if (
    /api[_\s-]?key|zugangsschlüssel|missing.*(key|secret)|kein.*(schlüssel|key)|fehlender.*(key|schlüssel)/i.test(
      roh,
    )
  ) {
    return "fehlender_api_schluessel";
  }

  if (/budget|tagesbudget|maxCost|cost.?limit|kostengrenze/i.test(roh)) {
    return "budgetgrenze";
  }

  if (s === "not_implemented") return "not_implemented";

  return "unbekannt";
}

function schluesselNameAusText(roh: string): string {
  const m =
    roh.match(/([A-Z][A-Z0-9_]{3,})/) ??
    roh.match(/Schlüssel\s*[„"']?([^„"'\s.]+)[„"']?/i) ??
    roh.match(/key[_\s-]?name[:\s]+([A-Za-z0-9_]+)/i);
  return m?.[1] ?? "unbekannt";
}

/**
 * Bildet die Anzeigezeile für das Statusband.
 */
export function fehlertextFuerBand(
  roh: string | null | undefined,
  status?: string | null,
): FehlertextErgebnis {
  const technisch = (roh ?? "").trim() || "Unbekannter Fehler";
  const klasse = erkenneFehlerklasse(technisch, status);

  switch (klasse) {
    case "timeout":
      return {
        klasse,
        anzeige:
          "Fehlgeschlagen: Der Schritt hat zu lange gebraucht (über 5 Minuten).",
        technisch,
      };
    case "fehlender_api_schluessel":
      return {
        klasse,
        anzeige: `Fehlgeschlagen: Für diesen Schritt fehlt ein Zugangsschlüssel (${schluesselNameAusText(technisch)}).`,
        technisch,
      };
    case "not_implemented":
      return {
        klasse,
        anzeige: "Übersprungen: Diese Funktion gibt es im Produkt noch nicht.",
        technisch,
      };
    case "budgetgrenze":
      return {
        klasse,
        anzeige: "Abgebrochen: Das Tagesbudget ist erreicht.",
        technisch,
      };
    default:
      return {
        klasse: "unbekannt",
        anzeige: `Fehlgeschlagen: ${ersteZeile(technisch)}`,
        technisch,
      };
  }
}
